import React, { useState, useEffect, useCallback } from 'react';
import OAuthConnectButton from './OAuthConnectButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PageIssue {
  type: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  destination?: string; // for broken links
}

interface EnrichedPage {
  url: string;
  statusCode: number;
  title: string;
  metaDescription: string;
  h1: string;
  wordCount: number;
  canonical: string;
  isNoindex: boolean;
  issues: PageIssue[];
  // GSC enrichment
  clicks?: number;
  impressions?: number;
  ctr?: number;
  avgPosition?: number;
  // GA4 enrichment
  sessions?: number;
  users?: number;
}

interface IssueCategory {
  label: string;
  severity: 'critical' | 'warning' | 'info';
  count: number;
  pages: string[];
}

interface AuditResult {
  overview: {
    totalPages: number;
    totalIssues: number;
    pagesWithGSC: number;
    pagesWithGA4: number;
  };
  issueCategories: Record<string, IssueCategory>;
  pages: EnrichedPage[];
}

interface GA4Property {
  propertyId: string;
  displayName: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

function truncateUrl(url: string, max = 55): string {
  if (!url) return '-';
  try {
    const u = new URL(url);
    const path = u.pathname + u.search;
    if (path.length <= max) return path;
    return path.slice(0, max - 3) + '...';
  } catch {
    if (url.length <= max) return url;
    return url.slice(0, max - 3) + '...';
  }
}

function truncateText(text: string, max = 45): string {
  if (!text) return '-';
  if (text.length <= max) return text;
  return text.slice(0, max - 3) + '...';
}

function statusColor(code: number): string {
  if (code >= 200 && code < 300) return 'bg-green-100 text-green-700';
  if (code >= 300 && code < 400) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

function severityColor(s: 'critical' | 'warning' | 'info') {
  if (s === 'critical') return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800', icon: 'text-red-500' };
  if (s === 'warning') return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-800', icon: 'text-yellow-500' };
  return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800', icon: 'text-blue-500' };
}

const Spinner: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Sort helpers                                                       */
/* ------------------------------------------------------------------ */

type SortKey = 'url' | 'statusCode' | 'title' | 'wordCount' | 'issues' | 'clicks' | 'impressions' | 'ctr' | 'avgPosition' | 'sessions' | 'users';
type SortDir = 'asc' | 'desc';

function getSortValue(page: EnrichedPage, key: SortKey): number | string {
  switch (key) {
    case 'url': return page.url.toLowerCase();
    case 'statusCode': return page.statusCode;
    case 'title': return (page.title || '').toLowerCase();
    case 'wordCount': return page.wordCount;
    case 'issues': return page.issues.length;
    case 'clicks': return page.clicks ?? -1;
    case 'impressions': return page.impressions ?? -1;
    case 'ctr': return page.ctr ?? -1;
    case 'avgPosition': return page.avgPosition ?? 999;
    case 'sessions': return page.sessions ?? -1;
    case 'users': return page.users ?? -1;
    default: return 0;
  }
}

function sortPages(pages: EnrichedPage[], key: SortKey, dir: SortDir): EnrichedPage[] {
  return [...pages].sort((a, b) => {
    const va = getSortValue(a, key);
    const vb = getSortValue(b, key);
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const SFSiteAudit: React.FC = () => {
  // Form state
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [limit, setLimit] = useState(500);

  // GSC state
  const [gscConnected, setGscConnected] = useState(false);
  const [gscProperties, setGscProperties] = useState<string[]>([]);
  const [gscSelectedProperty, setGscSelectedProperty] = useState('');
  const [gscPropsLoading, setGscPropsLoading] = useState(false);

  // GA4 state
  const [ga4Connected, setGa4Connected] = useState(false);
  const [ga4Properties, setGa4Properties] = useState<GA4Property[]>([]);
  const [ga4SelectedProperty, setGa4SelectedProperty] = useState('');
  const [ga4PropsLoading, setGa4PropsLoading] = useState(false);

  // Audit state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AuditResult | null>(null);

  // UI state
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('issues');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [issueModal, setIssueModal] = useState<{ key: string; category: IssueCategory } | null>(null);

  /* ---- Load GSC properties when connected ---- */
  const fetchGscProperties = useCallback(async () => {
    setGscPropsLoading(true);
    try {
      const res = await fetch('/api/seotools/sf-site-audit/gsc-properties', {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || `Failed to load GSC properties (${res.status})`);
      }
      const data = await res.json();
      const rawProps = Array.isArray(data) ? data : data.properties ?? [];
      const props: string[] = rawProps.map((p: any) => (typeof p === 'string' ? p : p.url || p.siteUrl || p));
      setGscProperties(props);
      if (props.length > 0 && !gscSelectedProperty) {
        setGscSelectedProperty(props[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load GSC properties');
    } finally {
      setGscPropsLoading(false);
    }
  }, [gscSelectedProperty]);

  useEffect(() => {
    if (gscConnected) {
      fetchGscProperties();
    } else {
      setGscProperties([]);
      setGscSelectedProperty('');
    }
  }, [gscConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- Load GA4 properties when connected ---- */
  const fetchGa4Properties = useCallback(async () => {
    setGa4PropsLoading(true);
    try {
      const res = await fetch('/api/seotools/sf-site-audit/ga4-properties', {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || `Failed to load GA4 properties (${res.status})`);
      }
      const data = await res.json();
      const rawProps = Array.isArray(data) ? data : data.properties ?? [];
      const props: GA4Property[] = rawProps.map((p: any) =>
        typeof p === 'string' ? { propertyId: p, displayName: p } : { propertyId: p.propertyId || p.name || p, displayName: p.displayName || p.propertyId || p.name || p }
      );
      setGa4Properties(props);
      if (props.length > 0 && !ga4SelectedProperty) {
        setGa4SelectedProperty(props[0].propertyId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load GA4 properties');
    } finally {
      setGa4PropsLoading(false);
    }
  }, [ga4SelectedProperty]);

  useEffect(() => {
    if (ga4Connected) {
      fetchGa4Properties();
    } else {
      setGa4Properties([]);
      setGa4SelectedProperty('');
    }
  }, [ga4Connected]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- Run Audit ---- */
  const runAudit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedUrl = websiteUrl.trim();
    const trimmedSitemap = sitemapUrl.trim();
    if (!trimmedUrl || !trimmedSitemap) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setExpandedCategories(new Set());
    setExpandedRows(new Set());

    try {
      const payload: Record<string, any> = {
        websiteUrl: trimmedUrl,
        sitemapUrl: trimmedSitemap,
        limit,
      };
      if (gscConnected && gscSelectedProperty) {
        payload.gscSiteUrl = gscSelectedProperty;
      }
      if (ga4Connected && ga4SelectedProperty) {
        payload.ga4PropertyId = ga4SelectedProperty;
      }

      const res = await fetch('/api/seotools/sf-site-audit/run', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || `Audit failed (${res.status})`);
      }

      const data: AuditResult = await res.json();
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  /* ---- Sort handler ---- */
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const toggleCategory = (key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const SortHeader: React.FC<{ label: string; colKey: SortKey; className?: string }> = ({ label, colKey, className = '' }) => (
    <th
      className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 ${className}`}
      onClick={() => handleSort(colKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === colKey && (
          <svg className={`h-3 w-3 ${sortDir === 'asc' ? '' : 'rotate-180'}`} fill="currentColor" viewBox="0 0 20 20">
            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
          </svg>
        )}
      </span>
    </th>
  );

  const hasGSCData = result ? result.overview.pagesWithGSC > 0 : false;
  const hasGA4Data = result ? result.overview.pagesWithGA4 > 0 : false;
  const sortedPages = result ? sortPages(result.pages, sortKey, sortDir) : [];

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800">SF Site Audit</h2>
        <p className="text-sm text-gray-500 mt-1">
          Comprehensive site audit combining crawl data with Google Search Console and Google Analytics enrichment.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
        <strong>Note:</strong> Large site audits may take 1-2 minutes. Optionally connect GSC and GA4 below for enriched data.
      </div>

      {/* ---------- Form ---------- */}
      <form onSubmit={runAudit} className="space-y-5">
        {/* URL inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="sf-website" className="block text-sm font-medium text-gray-700 mb-1">
              Website URL <span className="text-red-500">*</span>
            </label>
            <input
              id="sf-website"
              type="text"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
              required
            />
          </div>
          <div>
            <label htmlFor="sf-sitemap" className="block text-sm font-medium text-gray-700 mb-1">
              Sitemap URL <span className="text-red-500">*</span>
            </label>
            <input
              id="sf-sitemap"
              type="text"
              value={sitemapUrl}
              onChange={(e) => setSitemapUrl(e.target.value)}
              placeholder="https://example.com/sitemap.xml"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
              required
            />
          </div>
        </div>

        {/* Limit */}
        <div className="max-w-xs">
          <label htmlFor="sf-limit" className="block text-sm font-medium text-gray-700 mb-1">
            Crawl Limit
          </label>
          <input
            id="sf-limit"
            type="number"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value) || 500)}
            min={1}
            max={10000}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            disabled={loading}
          />
        </div>

        {/* OAuth connections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* GSC */}
          <div className="space-y-3">
            <OAuthConnectButton
              service="gsc"
              label="Google Search Console"
              onConnect={() => setGscConnected(true)}
              onDisconnect={() => setGscConnected(false)}
            />
            {gscConnected && (
              <div>
                <label htmlFor="sf-gsc-property" className="block text-sm font-medium text-gray-700 mb-1">
                  GSC Property
                </label>
                {gscPropsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Spinner /> Loading properties...
                  </div>
                ) : gscProperties.length > 0 ? (
                  <select
                    id="sf-gsc-property"
                    value={gscSelectedProperty}
                    onChange={(e) => setGscSelectedProperty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                  >
                    {gscProperties.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-500">No properties found.</p>
                )}
              </div>
            )}
          </div>

          {/* GA4 */}
          <div className="space-y-3">
            <OAuthConnectButton
              service="ga4"
              label="Google Analytics 4"
              onConnect={() => setGa4Connected(true)}
              onDisconnect={() => setGa4Connected(false)}
            />
            {ga4Connected && (
              <div>
                <label htmlFor="sf-ga4-property" className="block text-sm font-medium text-gray-700 mb-1">
                  GA4 Property
                </label>
                {ga4PropsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Spinner /> Loading properties...
                  </div>
                ) : ga4Properties.length > 0 ? (
                  <select
                    id="sf-ga4-property"
                    value={ga4SelectedProperty}
                    onChange={(e) => setGa4SelectedProperty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                  >
                    {ga4Properties.map((p) => (
                      <option key={p.propertyId} value={p.propertyId}>{p.displayName} ({p.propertyId})</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-500">No properties found.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Submit */}
        <div>
          <button
            type="submit"
            disabled={loading || !websiteUrl.trim() || !sitemapUrl.trim()}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Spinner /> Running Audit...
              </span>
            ) : (
              'Run Audit'
            )}
          </button>
        </div>
      </form>

      {/* ---------- Error ---------- */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* ---------- Loading indicator ---------- */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <Spinner className="h-8 w-8 text-blue-500" />
          <p className="text-sm text-gray-500">Crawling site and enriching data. This may take 1-2 minutes for large sites...</p>
        </div>
      )}

      {/* ---------- Results ---------- */}
      {result && !loading && (
        <>
          {/* (a) Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Pages Crawled</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{result.overview.totalPages}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Issues Found</p>
              <p className="text-2xl font-bold text-orange-700 mt-1">{result.overview.totalIssues}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Pages with GSC Data</p>
              <p className="text-2xl font-bold text-blue-700 mt-1">{result.overview.pagesWithGSC}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Pages with GA4 Data</p>
              <p className="text-2xl font-bold text-purple-700 mt-1">{result.overview.pagesWithGA4}</p>
            </div>
          </div>

          {/* (b) Issue Categories */}
          {Object.keys(result.issueCategories).length > 0 && (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-800">Issue Categories</h3>
              {Object.entries(result.issueCategories)
                .sort(([, a], [, b]) => {
                  const order = { critical: 0, warning: 1, info: 2 };
                  if (order[a.severity] !== order[b.severity]) return order[a.severity] - order[b.severity];
                  return b.count - a.count;
                })
                .map(([key, cat]) => {
                  const colors = severityColor(cat.severity);
                  const isOpen = expandedCategories.has(key);
                  return (
                    <div key={key} className={`border rounded-lg ${colors.border} ${colors.bg}`}>
                      <button
                        onClick={() => toggleCategory(key)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left"
                      >
                        <div className="flex items-center gap-3">
                          {/* Severity icon */}
                          {cat.severity === 'critical' && (
                            <svg className={`h-5 w-5 ${colors.icon}`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          )}
                          {cat.severity === 'warning' && (
                            <svg className={`h-5 w-5 ${colors.icon}`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          )}
                          {cat.severity === 'info' && (
                            <svg className={`h-5 w-5 ${colors.icon}`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          )}
                          <span className={`text-sm font-medium ${colors.text}`}>{cat.label}</span>
                          <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold ${colors.badge}`}>
                            {cat.count}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setIssueModal({ key, category: cat }); }}
                            className={`text-xs underline ${colors.text} hover:opacity-80`}
                          >
                            View URLs
                          </button>
                          <ChevronIcon open={isOpen} />
                        </div>
                      </button>
                      {isOpen && cat.pages.length > 0 && (
                        <div className="px-4 pb-3">
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {cat.pages.slice(0, 50).map((url, i) => (
                              <div key={i} className="text-xs text-gray-700 font-mono truncate" title={url}>
                                <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600">
                                  {truncateUrl(url, 90)}
                                </a>
                              </div>
                            ))}
                            {cat.pages.length > 50 && (
                              <p className="text-xs text-gray-400">...and {cat.pages.length - 50} more</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          {/* (c) Enriched Pages Table */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              All Pages ({result.pages.length})
            </h3>
            <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[650px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                    <SortHeader label="URL" colKey="url" />
                    <SortHeader label="Status" colKey="statusCode" />
                    <SortHeader label="Title" colKey="title" />
                    <SortHeader label="Words" colKey="wordCount" />
                    <SortHeader label="Issues" colKey="issues" />
                    {hasGSCData && (
                      <>
                        <SortHeader label="Clicks" colKey="clicks" />
                        <SortHeader label="Impr." colKey="impressions" />
                        <SortHeader label="CTR" colKey="ctr" />
                        <SortHeader label="Avg Pos" colKey="avgPosition" />
                      </>
                    )}
                    {hasGA4Data && (
                      <>
                        <SortHeader label="Sessions" colKey="sessions" />
                        <SortHeader label="Users" colKey="users" />
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedPages.map((page, idx) => {
                    const isExpanded = expandedRows.has(idx);
                    return (
                      <React.Fragment key={idx}>
                        <tr className="hover:bg-gray-50 transition-colors">
                          {/* Expand toggle */}
                          <td className="px-2 py-2 text-center">
                            {page.issues.length > 0 && (
                              <button onClick={() => toggleRow(idx)} className="text-gray-400 hover:text-gray-600">
                                <ChevronIcon open={isExpanded} />
                              </button>
                            )}
                          </td>
                          {/* URL */}
                          <td className="px-3 py-2 text-sm max-w-[280px]" title={page.url}>
                            <a href={page.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono text-xs">
                              {truncateUrl(page.url, 50)}
                            </a>
                          </td>
                          {/* Status */}
                          <td className="px-3 py-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono font-medium ${statusColor(page.statusCode)}`}>
                              {page.statusCode}
                            </span>
                          </td>
                          {/* Title */}
                          <td className="px-3 py-2 text-sm text-gray-700 max-w-[200px]" title={page.title}>
                            {truncateText(page.title, 40)}
                          </td>
                          {/* Word Count */}
                          <td className="px-3 py-2 text-sm text-gray-600 font-mono">
                            {page.wordCount}
                          </td>
                          {/* Issues */}
                          <td className="px-3 py-2">
                            {page.issues.length > 0 ? (
                              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                                {page.issues.length}
                              </span>
                            ) : (
                              <span className="text-xs text-green-600 font-medium">OK</span>
                            )}
                          </td>
                          {/* GSC columns */}
                          {hasGSCData && (
                            <>
                              <td className="px-3 py-2 text-sm text-gray-600 font-mono">{page.clicks ?? '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600 font-mono">{page.impressions ?? '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600 font-mono">
                                {page.ctr != null ? `${(page.ctr * 100).toFixed(1)}%` : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-600 font-mono">
                                {page.avgPosition != null ? page.avgPosition.toFixed(1) : '-'}
                              </td>
                            </>
                          )}
                          {/* GA4 columns */}
                          {hasGA4Data && (
                            <>
                              <td className="px-3 py-2 text-sm text-gray-600 font-mono">{page.sessions ?? '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600 font-mono">{page.users ?? '-'}</td>
                            </>
                          )}
                        </tr>
                        {/* Expanded issue detail row */}
                        {isExpanded && page.issues.length > 0 && (
                          <tr>
                            <td colSpan={6 + (hasGSCData ? 4 : 0) + (hasGA4Data ? 2 : 0)} className="px-6 py-3 bg-gray-50">
                              <div className="space-y-1.5">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Issues for this page</p>
                                {page.issues.map((issue, iIdx) => {
                                  const iColors = severityColor(issue.severity);
                                  return (
                                    <div key={iIdx} className={`inline-flex items-center gap-2 mr-2 mb-1 px-2.5 py-1 rounded text-xs font-medium ${iColors.badge}`}>
                                      {issue.severity === 'critical' && (
                                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                      {issue.severity === 'warning' && (
                                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                      {issue.severity === 'info' && (
                                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                      <span>{issue.message}</span>
                                      {issue.destination && (
                                        <span className="text-gray-500 font-mono">
                                          {' -> '}{truncateUrl(issue.destination, 40)}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* (d) Issue Detail Modal */}
      {issueModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setIssueModal(null)}>
          <div
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold ${severityColor(issueModal.category.severity).badge}`}>
                  {issueModal.category.count}
                </span>
                <h3 className="text-lg font-semibold text-gray-800">{issueModal.category.label}</h3>
              </div>
              <button onClick={() => setIssueModal(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Modal Body */}
            <div className="overflow-y-auto px-6 py-4 flex-1">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Affected URLs ({issueModal.category.pages.length})</p>
              <div className="space-y-2">
                {issueModal.category.pages.map((url, i) => (
                  <div key={i} className="flex items-center gap-2 py-1.5 px-3 bg-gray-50 rounded-lg">
                    <span className="text-xs text-gray-400 font-mono w-6 text-right shrink-0">{i + 1}.</span>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline font-mono truncate"
                      title={url}
                    >
                      {url}
                    </a>
                  </div>
                ))}
              </div>
            </div>
            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-3 border-t border-gray-200">
              <button
                onClick={() => setIssueModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SFSiteAudit;
